import * as React from 'react';
import dayGridPlugin from '@fullcalendar/daygrid';
import { EventInput } from '@fullcalendar/core';
import FullCalendar from '@fullcalendar/react';

import gtm from '../../../../shared/helpers/gtm';
import useWindowDimensions from '../../../../shared/hooks/useWindowDimensions';
import WeekNumber from './WeekNumber';
import {
  APP_ID,
  SCREEN_TABLET,
  WEBSITE_URL,
} from '../../../../shared/config/constants';

import { CalendarItem } from '../../types';
import Item from './Item';

import './style.scss';
import { useGetWasteStreamsQuery } from '../../../../shared/store/slices/WebsiteApi';

interface Props {
  items: CalendarItem[],
  date: Date,
}

const Calendar: React.FC<Props> = (props) => {
  const calendarRef = React.useRef<FullCalendar>(null);
  const { data: streams } = useGetWasteStreamsQuery(null);
  const { width } = useWindowDimensions();
  const { items, date } = props;

  // Update the Full Calendar date everytime the `date` prop gets updated.
  React.useEffect(() => {
    calendarRef.current?.getApi().gotoDate(date);
  }, [date]);

  return (
    <div className="Calendar">
      <FullCalendar
        ref={calendarRef}
        events={items}
        headerToolbar={false}
        eventDataTransform={(event) => {
          // The event parameter does not respect the type of the
          // items we've passed in the `events` prop. That's why were
          // forced to do type casting here.
          const typedEvent = event as CalendarItem;

          // Allways add a `date`, `type` and `className`.
          const transformedEvent: EventInput = {
            date: typedEvent.date,
            type: typedEvent.type,
            className: `fc-event--${typedEvent.type}`,
          };

          // If it's a holiday event, add the holiday as the `title`.
          if (typedEvent.type === 'holiday') {
            transformedEvent.title = typedEvent.holiday;
          }

          // If it's a calendar item, and we have content available for it,
          // add the `url` and `title`.
          if (typedEvent.type === 'pickupMoment' && streams?.[typedEvent.wasteStreamId]) {
            const { wasteStreamId } = typedEvent;

            transformedEvent.wasteStreamId = wasteStreamId;
            transformedEvent.url = `${WEBSITE_URL}${streams?.[wasteStreamId].path}`;
            transformedEvent.title = streams?.[wasteStreamId].title;
          }

          return transformedEvent;
        }}
        weekNumberContent={WeekNumber}
        eventContent={Item}
        weekNumbers={width > SCREEN_TABLET}
        plugins={[dayGridPlugin]}
        initialView="dayGridMonth"
        eventClick={(e) => {
          if (e.event.extendedProps.wasteStreamId) {
            // Cancel the default navigation event.
            e.jsEvent.preventDefault();

            // Fire the GTM tracking event.
            gtm.trackUserEvent(String(APP_ID), 'Calendar inner waste stream click', String(streams?.[Number(e.event.extendedProps.wasteStreamId)].title));

            // Navigate manually anyways.
            if (e.event.url) {
              window.location.href = e.event.url;
            }
          }
        }}
        eventBackgroundColor="transparent"
        eventBorderColor="transparent"
        locale="nl"
        dayHeaderClassNames="Calendar__th"
        height="auto"
        eventInteractive
        firstDay={1}
        dayHeaderFormat={{
          weekday: width > SCREEN_TABLET ? 'long' : 'short',
        }}
      />
    </div>
  );
};

export default Calendar;
